import React from 'react';

// Country → ISO2 code map for flagcdn.com (which serves clean SVG flags
// for every country, free, no API key). UK constituent countries get
// their dedicated regional codes (`gb-eng`, `gb-sct`, `gb-wls`, `gb-nir`).
const ISO = {
  // British Isles
  'England': 'gb-eng', 'Scotland': 'gb-sct', 'Wales': 'gb-wls', 'Northern Ireland': 'gb-nir',
  'United Kingdom': 'gb', 'Great Britain': 'gb', 'UK': 'gb',
  'Ireland': 'ie', 'Republic of Ireland': 'ie',
  // Europe — major football nations
  'Spain': 'es', 'France': 'fr', 'Germany': 'de', 'Italy': 'it',
  'Portugal': 'pt', 'Netherlands': 'nl', 'Belgium': 'be', 'Croatia': 'hr',
  'Switzerland': 'ch', 'Sweden': 'se', 'Norway': 'no', 'Denmark': 'dk',
  'Finland': 'fi', 'Iceland': 'is', 'Austria': 'at', 'Czech Republic': 'cz',
  'Czechia': 'cz', 'Poland': 'pl', 'Hungary': 'hu', 'Slovakia': 'sk',
  'Slovenia': 'si', 'Romania': 'ro', 'Bulgaria': 'bg', 'Serbia': 'rs',
  'Bosnia and Herzegovina': 'ba', 'Bosnia': 'ba', 'Montenegro': 'me',
  'North Macedonia': 'mk', 'Albania': 'al', 'Greece': 'gr', 'Turkey': 'tr',
  'Türkiye': 'tr', 'Ukraine': 'ua', 'Russia': 'ru', 'Belarus': 'by',
  'Estonia': 'ee', 'Latvia': 'lv', 'Lithuania': 'lt', 'Cyprus': 'cy',
  'Malta': 'mt', 'Luxembourg': 'lu', 'Andorra': 'ad', 'Liechtenstein': 'li',
  'San Marino': 'sm', 'Monaco': 'mc', 'Vatican City': 'va', 'Moldova': 'md',
  'Georgia': 'ge', 'Armenia': 'am', 'Azerbaijan': 'az', 'Kazakhstan': 'kz',
  'Kosovo': 'xk', 'Faroe Islands': 'fo', 'Gibraltar': 'gi',
  // Americas
  'Brazil': 'br', 'Argentina': 'ar', 'Uruguay': 'uy', 'Chile': 'cl',
  'Colombia': 'co', 'Peru': 'pe', 'Ecuador': 'ec', 'Paraguay': 'py',
  'Bolivia': 'bo', 'Venezuela': 've', 'United States': 'us', 'USA': 'us',
  'Canada': 'ca', 'Mexico': 'mx', 'Costa Rica': 'cr', 'Panama': 'pa',
  'Honduras': 'hn', 'El Salvador': 'sv', 'Guatemala': 'gt', 'Nicaragua': 'ni',
  'Jamaica': 'jm', 'Trinidad and Tobago': 'tt', 'Haiti': 'ht',
  'Dominican Republic': 'do', 'Cuba': 'cu',
  // Africa
  'Senegal': 'sn', 'Morocco': 'ma', 'Egypt': 'eg', 'Tunisia': 'tn',
  'Algeria': 'dz', 'Nigeria': 'ng', 'Ghana': 'gh', "Côte d'Ivoire": 'ci',
  'Ivory Coast': 'ci', 'Cameroon': 'cm', 'South Africa': 'za',
  'Kenya': 'ke', 'Ethiopia': 'et', 'Mali': 'ml', 'Burkina Faso': 'bf',
  'DR Congo': 'cd', 'Congo': 'cg', 'Angola': 'ao', 'Zambia': 'zm',
  'Zimbabwe': 'zw', 'Tanzania': 'tz', 'Uganda': 'ug', 'Sudan': 'sd',
  'Libya': 'ly', 'Mauritania': 'mr', 'Madagascar': 'mg', 'Mozambique': 'mz',
  'Cape Verde': 'cv', 'Gabon': 'ga', 'Guinea': 'gn', 'Sierra Leone': 'sl',
  'Liberia': 'lr', 'Togo': 'tg', 'Benin': 'bj', 'Namibia': 'na',
  'Botswana': 'bw', 'Rwanda': 'rw', 'Burundi': 'bi', 'Niger': 'ne',
  'Chad': 'td', 'Central African Republic': 'cf', 'Somalia': 'so',
  'Eritrea': 'er', 'South Sudan': 'ss', 'Lesotho': 'ls', 'Eswatini': 'sz',
  'Comoros': 'km', 'Mauritius': 'mu', 'Seychelles': 'sc',
  // Middle East
  'Saudi Arabia': 'sa', 'United Arab Emirates': 'ae', 'UAE': 'ae',
  'Qatar': 'qa', 'Kuwait': 'kw', 'Bahrain': 'bh', 'Oman': 'om',
  'Yemen': 'ye', 'Iraq': 'iq', 'Iran': 'ir', 'Israel': 'il',
  'Palestine': 'ps', 'Jordan': 'jo', 'Lebanon': 'lb', 'Syria': 'sy',
  // Asia
  'Japan': 'jp', 'South Korea': 'kr', 'Korea Republic': 'kr',
  'North Korea': 'kp', 'China': 'cn', 'India': 'in', 'Indonesia': 'id',
  'Vietnam': 'vn', 'Thailand': 'th', 'Malaysia': 'my', 'Singapore': 'sg',
  'Philippines': 'ph', 'Cambodia': 'kh', 'Myanmar': 'mm', 'Laos': 'la',
  'Brunei': 'bn', 'Mongolia': 'mn', 'Nepal': 'np', 'Bangladesh': 'bd',
  'Sri Lanka': 'lk', 'Pakistan': 'pk', 'Afghanistan': 'af',
  'Uzbekistan': 'uz', 'Kyrgyzstan': 'kg', 'Tajikistan': 'tj',
  'Turkmenistan': 'tm', 'Hong Kong': 'hk', 'Taiwan': 'tw', 'Macau': 'mo',
  // Oceania
  'Australia': 'au', 'New Zealand': 'nz', 'Fiji': 'fj', 'Papua New Guinea': 'pg',
  'Samoa': 'ws', 'Tonga': 'to', 'Solomon Islands': 'sb', 'Vanuatu': 'vu',
  'New Caledonia': 'nc', 'Tahiti': 'pf',
};

const iso = (country) => ISO[country] || ISO[country?.replace(/&/g, 'and')] || null;

/**
 * Watermark flag that sits *behind* content (absolute, low opacity).
 * Use inside a `relative overflow-hidden` parent.
 * Source: flagcdn.com (CC0 SVGs, no API key required).
 */
export const FlagBackdrop = ({ country, opacity = 0.18, className = '' }) => {
  const code = iso(country);
  if (!code) return null;
  return (
    <img
      src={`https://flagcdn.com/${code}.svg`}
      alt=""
      aria-hidden
      draggable={false}
      data-testid="flag-backdrop"
      className={`absolute inset-0 w-full h-full object-cover pointer-events-none select-none ${className}`}
      style={{
        opacity,
        filter: 'saturate(1.25) contrast(1.05)',
        WebkitMaskImage:
          'linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%)',
        maskImage:
          'linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%)',
      }}
    />
  );
};

/** Small inline flag chip (24×16). Falls back to emoji when ISO is unknown. */
export const FlagChip = ({ country, emoji, size = 24, className = '' }) => {
  const code = iso(country);
  if (!code) {
    return <span className={`inline-block leading-none ${className}`} aria-label={country}>{emoji || '🏳️'}</span>;
  }
  return (
    <img
      src={`https://flagcdn.com/${code}.svg`}
      alt={country}
      draggable={false}
      style={{ width: size, height: Math.round(size * 0.66), objectFit: 'cover', borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.10)' }}
      className={`inline-block ${className}`}
    />
  );
};
